import React, { useState, useMemo, useRef } from "react";
import { useStore } from "zustand";
import { isPossiblePhoneNumber, type Country } from "react-phone-number-input";
import { safeParse as vParse, object as vObject } from "valibot";
import { X, Upload } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multiple-selector";
import { useConfigStore, usePageStore } from "@/stores/page";
import { SITE_TITLE } from "@/constants";
import { convertFieldNameToKey, convertFieldName, cn } from "@/lib/utils";
import { saveBrandKit } from "@/lib/api/client";
import { fieldSchema, emailSchema } from "@/lib/api/schema";
import { AgentMapping } from "@/constants";
import { useTranslation } from "react-i18next";
import {
  FormFieldType,
  type TagItemProps,
  type FieldDropdownProps,
  type FieldFileProps,
  type FieldCheckboxProps
} from "@/types/buildAgent";
import { disclaimerMarkdownComponents } from "@/components/AgentChat";

export interface LeadFormField {
  type: string;
  placeholder: string;
  required: boolean;
  component: typeof Checkbox;
  value: boolean | string;
  text?: string;
}

export const DEFAULT_FIELDS = ["first_name", "last_name", "email"];
let isCountryCodeChange = false;

// Type guard functions
const isDropdownField = (field: TagItemProps): field is FieldDropdownProps => {
  return field.type === FormFieldType.Dropdown;
};

const isFileField = (field: TagItemProps): field is FieldFileProps => {
  return field.type === FormFieldType.File;
};

const isCheckboxField = (field: TagItemProps): field is FieldCheckboxProps => {
  return field.type === FormFieldType.Checkbox;
};

// File upload component based on Figma design
const FileUploadField = ({
  label,
  required,
  fileTypes = ["PDF", "DOC", "DOCX", "CSV"],
  maxSize = 5,
  multiple = false,
  placeholder,
  onChange
}: {
  label: string;
  required: boolean;
  fileTypes?: string[];
  maxSize?: number;
  multiple: boolean;
  placeholder?: string;
  onChange: (...args: any[]) => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      const isValidType = fileTypes.some((type) =>
        file.name.toLowerCase().endsWith(type.toLowerCase())
      );
      const isValidSize = file.size <= maxSize * 1024 * 1024;
      return isValidType && isValidSize;
    });
    onChange(label, validFiles);
    setSelectedFiles(validFiles);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearFiles = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFiles([]);
    onChange(label, []);
    fileInputRef.current!.value = "";
  };

  return (
    <div className="inline-flex w-full flex-col gap-1">
      <span className="text-sm font-medium text-gray-700 capitalize">
        {convertFieldName(label)}
        {required && <b className="ml-2 text-red-500">*</b>}
      </span>
      <div
        className={cn(
          "flex cursor-pointer items-center gap-2 rounded border border-dashed border-slate-200 px-2.5 py-1 transition-colors hover:border-slate-300"
        )}
        onClick={handleClick}
      >
        <div className="relative min-w-0 flex-1">
          <span
            className={cn("block truncate text-sm text-gray-600")}
            title={
              selectedFiles.length > 0
                ? selectedFiles.map((f) => f.name).join("\n")
                : placeholder || "Choose file"
            }
          >
            {selectedFiles.length > 0
              ? (() => {
                  const truncateFileName = (
                    name: string,
                    maxLength: number = 20
                  ) => {
                    if (name.length <= maxLength) return name;
                    const extension = name.split(".").pop();
                    const nameWithoutExt = name.substring(
                      0,
                      name.lastIndexOf(".")
                    );
                    const truncatedName = nameWithoutExt.substring(
                      0,
                      maxLength - 3
                    );
                    return `${truncatedName}...${extension ? `.${extension}` : ""}`;
                  };

                  if (selectedFiles.length === 1) {
                    return truncateFileName(selectedFiles[0].name);
                  } else if (selectedFiles.length === 2) {
                    const name1 = truncateFileName(selectedFiles[0].name, 15);
                    const name2 = truncateFileName(selectedFiles[1].name, 15);
                    return `${name1}, ${name2}`;
                  } else {
                    const name1 = truncateFileName(selectedFiles[0].name, 12);
                    const name2 = truncateFileName(selectedFiles[1].name, 12);
                    return `${name1}, ${name2} +${selectedFiles.length - 2} more`;
                  }
                })()
              : placeholder || "Choose file"}
          </span>
        </div>
        {selectedFiles.length > 0 ? (
          <button
            onClick={handleClearFiles}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-100 text-red-600 transition-colors hover:bg-red-200"
            title="Clear selection"
          >
            <span className="text-xs">×</span>
          </button>
        ) : (
          <Upload className={cn("ml-auto h-4 w-4 text-[#007af5]")} />
        )}
      </div>
      <span className="text-xs text-gray-500">
        Supports {fileTypes.join(", ")} Max {maxSize}MB each.
      </span>
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={fileTypes.map((type) => `.${type.toLowerCase()}`).join(",")}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

// Checkbox field component based on Figma design
const CheckboxField = ({
  id,
  label,
  required,
  content,
  value,
  onChange
}: {
  id: string;
  label: string;
  required: boolean;
  content?: string;
  value?: boolean;
  onChange: (checked: boolean) => void;
}) => {
  const [checked, setChecked] = useState(value || false);

  // 同步外部值变化
  React.useEffect(() => {
    setChecked(value || false);
  }, [value]);

  const handleChange = (checked: boolean | "indeterminate") => {
    const booleanValue = checked === true;
    setChecked(booleanValue);
    onChange(booleanValue);
  };

  return (
    <div className="inline-flex w-full flex-col gap-1">
      <div className="relative flex gap-2">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={handleChange}
          className="mt-1 rounded border-gray-300"
        />
        <label
          htmlFor={id}
          className="inline-flex cursor-pointer gap-x-2 text-sm text-gray-700"
        >
          {required && (
            <b className="text-red-500" style={{ lineHeight: "20px" }}>
              *
            </b>
          )}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={disclaimerMarkdownComponents(true)}
          >
            {content || label}
          </ReactMarkdown>
        </label>
      </div>
    </div>
  );
};

// Dropdown field component based on Figma design
const DropdownField = ({
  label,
  required,
  options,
  allow_multiple = false,
  value,
  placeholder,
  onChange
}: {
  label: string;
  required: boolean;
  options: { value: string; label: string; selected: boolean }[];
  allow_multiple?: boolean;
  value: string | string[];
  placeholder?: string;
  onChange: (value: string | string[]) => void;
}) => {
  // 转换选项格式为 MultiSelect 需要的格式
  const selectorOptions = options.map((option) => ({
    value: option.value,
    label: option.label
  }));

  if (allow_multiple) {
    return (
      <div className="inline-flex w-full flex-col gap-1">
        <span className="text-sm font-medium text-gray-700 capitalize">
          {convertFieldName(label)}
          {required && <b className="ml-2 text-red-500">*</b>}
        </span>
        <MultiSelect
          options={selectorOptions}
          onValueChange={(selectedValues) => {
            onChange(selectedValues);
          }}
          defaultValue={Array.isArray(value) ? value : []}
          placeholder={placeholder || "Select options"}
          emptyIndicator={
            <p className="text-center text-lg leading-10 text-gray-600 dark:text-gray-400">
              No results found.
            </p>
          }
          maxCount={2}
          className="w-auto border-slate-200 bg-white text-sm"
        />
      </div>
    );
  }

  // 单选模式使用原来的 Select 组件
  return (
    <div className="inline-flex w-full flex-col gap-1">
      <span className="text-sm font-medium text-gray-700 capitalize">
        {convertFieldName(label)}
        {required && <b className="ml-2 text-red-500">*</b>}
      </span>
      <Select
        value={Array.isArray(value) ? value[0] || "" : value}
        onValueChange={(selectedValue) => {
          onChange(selectedValue);
        }}
      >
        <SelectTrigger className="w-full border-slate-200 bg-white text-sm text-gray-600 ring-0">
          <SelectValue placeholder={placeholder || "Select a option"} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="capitalize"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
};

// Text field component with icon support
const TextFieldWithIcon = ({
  label,
  required,
  placeholder,
  type = "text",
  icon: Icon,
  value,
  onChange
}: {
  label: string;
  required: boolean;
  placeholder: string;
  type?: string;
  icon?: React.ComponentType<{ className?: string }>;
  value: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="inline-flex w-full flex-col gap-1">
      <span className="text-sm font-medium text-gray-700 capitalize">
        {convertFieldName(label)}
        {required && <b className="ml-2 text-red-500">*</b>}
      </span>
      <div className="relative">
        {Icon && (
          <div className="absolute top-1/2 left-3 -translate-y-1/2 transform">
            <Icon className="h-4 w-4 text-gray-400" />
          </div>
        )}
        <Input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "w-full rounded border-slate-200 p-2 text-sm",
            Icon && "pl-10"
          )}
        />
      </div>
    </div>
  );
};

const LeadForm = ({
  agentId,
  required,
  requireId,
  fields: customFields,
  onClose,
  onChange = () => {}
}: {
  agentId: string;
  required: boolean;
  requireId: boolean;
  fields?: TagItemProps[];
  conversationId?: string;
  onClose: (isSubmitted: boolean) => void;
  onChange?: (...args: any[]) => void;
}) => {
  const { t } = useTranslation("translations");
  const configStore = useConfigStore();
  const appearance = useStore(
    configStore,
    (state) => state.brandKit.appearance_config
  );
  const leadContactFields = useStore(
    configStore,
    (state) => state.config.lead_contact_fields
  );

  // 使用传入的字段或全局字段
  const fieldsToUse = customFields || leadContactFields.fields || [];
  const pageStore = usePageStore();
  const agentMode = useStore(pageStore, (state) => state.agentMode);
  const agent = useStore(pageStore, (state) => state.agent);
  const appearanceConfig = appearance[agentMode];
  const [issues, setIssues] = useState<string[]>([]);
  const [chooseId, setChooseId] = useState("");

  // Dynamic form field values based on configuration
  const [formValues, setFormValues] = useState<
    Record<string, string | string[] | File[] | boolean>
  >(() => {
    const initialValues: Record<string, string | string[] | File[] | boolean> =
      {};
    fieldsToUse.forEach((field) => {
      if (isFileField(field)) {
        initialValues[field.label] = [];
      } else if (isCheckboxField(field)) {
        initialValues[field.label] = false;
      } else if (isDropdownField(field)) {
        initialValues[field.label] = field.config.allow_multiple ? [] : "";
      } else {
        initialValues[field.label] = "";
      }
    });
    return initialValues;
  });

  const agentSelect = useMemo(
    () =>
      Object.entries(AgentMapping[agentId] || {}).map((item) => ({
        label: item[0],
        value: item[1] as string
      })),
    [agentId]
  );
  const brandKit = useStore(configStore, (state) => state.brandKit);
  const [countryCode] = useState<Country>(
    () => brandKit.custom_fields?.phone_country || "US"
  );

  let saveCountryCode: Country = "US";
  const updateBrandKit = useStore(configStore, (state) => state.updateBrandKit);

  const allFields = fieldsToUse;

  const brandLogo = appearanceConfig.brand_logo.url || "/images/logo.svg";
  const brandName = appearanceConfig.name || agent?.name || SITE_TITLE;

  // Check if form is valid for submit button - Dynamic validation
  const isFormValid = useMemo(() => {
    // Check all required fields are filled
    const hasRequiredFields = allFields
      .filter((field) => field.required)
      .every((field) => {
        const value = formValues[field.label];
        if (isCheckboxField(field)) {
          return value === true;
        } else if (isFileField(field)) {
          return Array.isArray(value) && value.length > 0;
        } else if (isDropdownField(field)) {
          return field.config.allow_multiple
            ? Array.isArray(value) && value.length > 0
            : value !== "";
        } else {
          return typeof value === "string" && value.trim() !== "";
        }
      });

    return hasRequiredFields && (agentSelect.length === 0 || chooseId !== "");
  }, [formValues, chooseId, agentSelect.length, allFields]);

  const handleSubmit = (e) => {
    if (!requireId) return;
    e.preventDefault();
    e.stopPropagation();
    const formData = {};
    const requiredValues = {};
    const requiredValuesValid = {};

    // Validate phone fields
    for (const field of allFields) {
      if (field.type === FormFieldType.Phone) {
        const phoneValue = formValues[field.label] as string;
        if (field.required && !isPossiblePhoneNumber(phoneValue)) {
          setIssues(["Invalid phone number"]);
          return;
        }
      }
    }

    // Validate checkbox fields - must be checked if required
    for (const field of allFields) {
      if (isCheckboxField(field) && field.required) {
        const checkboxValue = formValues[field.label] as boolean;
        if (!checkboxValue) {
          setIssues([`${field.label} must be checked`]);
          return;
        }
      }
    }

    // Process all fields
    for (const field of allFields) {
      const val = formValues[field.label];

      // Convert field values to string format for validation and storage
      let processedValue: string;
      if (isCheckboxField(field)) {
        // Convert boolean to string
        processedValue = val ? "true" : "false";
      } else if (
        (isDropdownField(field) && field.config.allow_multiple) ||
        isFileField(field)
      ) {
        // Convert array to comma-separated string
        processedValue = Array.isArray(val)
          ? val.join(", ")
          : String(val || "");
      } else {
        // Keep as string
        processedValue = String(val || "");
      }

      if (![FormFieldType.Checkbox].includes(field.type)) {
        formData[
          field.type === FormFieldType.Phone
            ? "phone"
            : convertFieldNameToKey(field.label)
        ] = processedValue;
      }

      if (field.required) {
        requiredValues[convertFieldNameToKey(field.label)] = processedValue;
        if (field.type === FormFieldType.Email) {
          requiredValuesValid[convertFieldNameToKey(field.label)] = emailSchema;
        } else {
          requiredValuesValid[convertFieldNameToKey(field.label)] = fieldSchema(
            field.label
          );
        }
      }
    }

    if (agentSelect.length > 0 && chooseId) {
      formData["agent_id"] = chooseId;
    }
    localStorage.setItem("form_data", JSON.stringify(formData));

    // @ts-ignore
    const requiredValid = vObject(requiredValuesValid);
    const parsed = vParse(requiredValid, requiredValues);
    if (agentSelect.length > 0 && !chooseId) {
      setIssues([t("agent.form.useCaseRequired")]);
      return;
    }
    if (!parsed.success) {
      setIssues(parsed.issues.map((v) => v.message));
      return;
    }
    onClose(true);
  };

  const onCountryChange = (phone_country_code: string) => {
    if (requireId) return;
    const newBrandKit = { ...brandKit };
    newBrandKit.custom_fields = {
      ...newBrandKit.custom_fields,
      phone_country: saveCountryCode,
      phone_country_code
    };
    updateBrandKit(newBrandKit);
    saveBrandKit(agentId, newBrandKit);
  };

  // Dynamic field renderer
  const renderField = (field: TagItemProps) => {
    const value = formValues[field.label];
    const updateValue = (newValue: string | string[] | File[] | boolean) => {
      setFormValues((prev) => ({ ...prev, [field.label]: newValue }));
    };

    switch (field.type) {
      case FormFieldType.Text:
      case FormFieldType.Email:
        return (
          <TextFieldWithIcon
            key={field.label}
            label={field.label}
            required={field.required}
            placeholder={field.placeholder}
            type={field.type === FormFieldType.Email ? "email" : "text"}
            value={(value as string) || ""}
            onChange={updateValue}
          />
        );

      case FormFieldType.Phone:
        return (
          <div key={field.label} className="inline-flex w-full flex-col gap-1">
            <span className="text-sm font-medium text-gray-700 capitalize">
              {convertFieldName(field.label)}
              {field.required && <b className="ml-2 text-red-500">*</b>}
            </span>
            <PhoneInput
              value={(value as string) || ""}
              onChange={(phoneValue) => {
                if (isCountryCodeChange) {
                  isCountryCodeChange = false;
                  onCountryChange(phoneValue);
                }
                updateValue(phoneValue);
              }}
              onCountryChange={(country) => {
                isCountryCodeChange = true;
                saveCountryCode = country as Country;
              }}
              international
              defaultCountry={countryCode}
              className="w-full"
            />
          </div>
        );

      case FormFieldType.Dropdown:
        if (isDropdownField(field)) {
          return (
            <DropdownField
              key={field.label}
              label={field.label}
              required={field.required}
              placeholder={field.placeholder}
              options={field.config.options}
              allow_multiple={field.config.allow_multiple}
              value={
                (value as string | string[]) ||
                (field.config.allow_multiple ? [] : "")
              }
              onChange={updateValue}
            />
          );
        }
        break;

      case FormFieldType.File:
        if (isFileField(field)) {
          return (
            <FileUploadField
              key={field.label}
              label={field.label}
              required={field.required}
              fileTypes={field.config.allowed_file_types}
              maxSize={field.config.max_file_size / (1024 * 1024)} // Convert bytes to MB
              onChange={(label: string, files: File[]) => {
                onChange(label, files);
                updateValue(files.map((v) => v.name));
              }}
              multiple={field.config.allow_multiple_files}
              placeholder={field.placeholder}
            />
          );
        }
        break;

      case FormFieldType.Checkbox:
        if (isCheckboxField(field)) {
          return (
            <CheckboxField
              key={field.label}
              id={field.label}
              label={field.label}
              required={field.required}
              content={field.config.checkbox_text}
              value={value as boolean}
              onChange={updateValue}
            />
          );
        }
        break;

      default:
        return null;
    }
  };

  return (
    <form
      className={`mx-auto flex w-full max-w-[352px] flex-1 flex-col gap-6 overflow-y-auto rounded-xl bg-white p-6 shadow-[0px_0px_12.2px_0px_rgba(0,0,0,0.08)] outline-1 outline-offset-[-1px] outline-slate-200`}
      onSubmit={handleSubmit}
      style={{
        maxHeight: "90%",
        scrollbarWidth: "none"
      }}
      data-testid="lead-form"
    >
      <div
        className="flex h-10 items-center gap-3"
        data-testid="lead-form-header"
      >
        <div
          className="flex h-full w-10 items-center justify-center rounded-sm"
          data-testid="lead-form-logo"
        >
          <img
            src={brandLogo}
            alt="Astra Logo"
            className="h-full w-full object-contain"
          />
        </div>
        <h4
          className="text-sm font-semibold text-black"
          data-testid="lead-form-title"
        >
          {brandName}
        </h4>
        <X
          className={cn(
            "ml-auto h-3 w-3 cursor-pointer text-[#a0aec0]",
            required && "hidden"
          )}
          onClick={() => onClose(false)}
          data-testid="lead-form-close-button"
        />
      </div>
      <div className="flex flex-col gap-4" data-testid="lead-form-fields">
        {/* Use case dropdown - Figma design */}
        {agentSelect.length > 0 && (
          <div className="inline-flex w-full flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">
              Use case for using WATI
              <b className="ml-2 text-red-500">*</b>
            </span>
            <Select
              value={chooseId}
              onValueChange={(value) => setChooseId(value)}
            >
              <SelectTrigger className="w-full border-slate-200 bg-white text-sm text-gray-600 ring-0">
                <SelectValue placeholder={t("agent.form.useCasePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {[
                    ...agentSelect,
                    { label: t("agent.form.others"), value: agentId }
                  ].map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className="capitalize"
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Dynamic fields based on configuration */}
        {allFields.map((field) => renderField(field))}
      </div>

      <ol
        className="list-disc px-3 text-xs text-red-500"
        data-testid="lead-form-errors"
      >
        {issues.map((v, index) => (
          <li key={v} data-testid={`error-item-${index}`}>
            {v}
          </li>
        ))}
      </ol>

      <Button
        className={cn(
          "w-full rounded-md py-2 text-sm font-medium text-white transition-opacity",
          isFormValid
            ? "bg-blue-600 hover:bg-blue-700"
            : "cursor-not-allowed bg-blue-400 opacity-50"
        )}
        type="submit"
        disabled={!isFormValid}
        data-testid="lead-form-submit-button"
      >
        {t("common.submit")}
      </Button>
    </form>
  );
};

export default LeadForm;
